import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PublicOnly, RequireAuth } from './components/RouteGuards';
import { Toasts } from './components/Toasts';
import { de } from './i18n/de';
import { AboutPage, ImprintPage, PrivacyPage } from './pages/InfoPages';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPage } from './pages/ResetPage';

// Arbeitsfläche (Zeichenfläche, Editor) und Profil laden erst, wenn man sie öffnet: Startseite und Anmeldung bleiben schlank
const ProjectPage = lazy(() => import('./pages/ProjectPage').then((m) => ({ default: m.ProjectPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));

const later = (page: ReactNode): ReactNode => <Suspense fallback={<div className="page muted">{de.common.loading}</div>}>{page}</Suspense>;

/**
 * Öffentlich: Startseite (`/`), Anmeldung, Registrierung, Passwort und die Infoseiten der Fußzeile (Über, Impressum, Datenschutz). Nur angemeldet: Übersicht der Geschichten (`/app`), eine
 * Geschichte (`/p/:id`) und das Profil – abgemeldet geht es von dort zur Anmeldung und danach wieder zurück.
 */
/** Fehlergrenze je Seite: ein Wechsel der Adresse beginnt neu (sonst bliebe der Fehler auch nach „Zur Übersicht“ stehen) */
function RouteBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

export function App() {
  return (
    <BrowserRouter>
      <RouteBoundary>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={
              <PublicOnly>
                <LoginPage />
              </PublicOnly>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnly>
                <RegisterPage />
              </PublicOnly>
            }
          />
          <Route path="/reset" element={<ResetPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/impressum" element={<ImprintPage />} />
          <Route path="/datenschutz" element={<PrivacyPage />} />
          <Route element={<RequireAuth />}>
            <Route path="/app" element={<ProjectsPage />} />
            <Route path="/p/:projectId" element={later(<ProjectPage />)} />
            <Route path="/profile" element={later(<ProfilePage />)} />
            {/* frühere Adresse des Profils (hieß „Konto“) */}
            <Route path="/settings" element={<Navigate to="/profile" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RouteBoundary>
      <Toasts />
    </BrowserRouter>
  );
}
